import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../components/modal/ModalContext';
import AlertModal from '../../components/modal/AlertModal';
import ConfirmModal from '../../components/modal/ConfirmModal';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

export default function CompanyBilling() {
  const { user } = useAuth();
  const { openModal, closeModal } = useModal();
  const [subscription, setSubscription] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [entitlements, setEntitlements] = useState<any>(null);
  const [topupProducts, setTopupProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [subRes, plansRes, invRes, entRes, topupRes] = await Promise.all([
          fetch(`/api/billing/subscription?company_id=${user?.company_id || ''}`, { headers: authHeaders() }),
          fetch('/api/billing/plans'),
          fetch(`/api/billing/invoices?company_id=${user?.company_id || ''}`, { headers: authHeaders() }),
          fetch('/api/billing/entitlements', { headers: authHeaders() }),
          fetch('/api/billing/topup-products', { headers: authHeaders() }),
        ]);
        const [subData, plansData, invData, entData, topupData] = await Promise.all([
          subRes.json(), plansRes.json(), invRes.json(), entRes.json(), topupRes.json(),
        ]);
        if (subData.success) setSubscription(subData.subscription);
        if (plansData.success) setPlans(plansData.plans || []);
        if (invData.success) setInvoices(invData.invoices || []);
        if (entData.success) setEntitlements(entData.summary);
        if (topupData.success) setTopupProducts(topupData.products || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, [user]);

  const friendlyError = (msg: string) => {
    if (msg?.includes('not configured') || msg?.includes('Admin Settings')) {
      return 'Billing is not available yet. Please contact your system administrator.';
    }
    return msg;
  };

  const handleCheckout = async (planId: string) => {
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ plan_id: planId, company_id: user?.company_id }),
      });
      const d = await res.json();
      if (d.success && d.url) window.location.href = d.url;
      else openModal({ component: AlertModal, props: { title: 'Checkout Unavailable', message: friendlyError(d.message) || 'Checkout is not available at the moment.', type: 'info' } });
    } catch { openModal({ component: AlertModal, props: { title: 'Checkout Unavailable', message: 'Checkout is not available at the moment. Please try again later.', type: 'info' } }); }
  };

  const handlePortal = async () => {
    try {
      const res = await fetch('/api/billing/portal', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ company_id: user?.company_id }),
      });
      const d = await res.json();
      if (d.success && d.url) window.location.href = d.url;
      else openModal({ component: AlertModal, props: { title: 'Portal Unavailable', message: friendlyError(d.message) || 'Billing portal is not available at the moment.', type: 'info' } });
    } catch { openModal({ component: AlertModal, props: { title: 'Portal Unavailable', message: 'Billing portal is not available at the moment.', type: 'info' } }); }
  };

  const handleCancel = () => {
    const modalId = openModal({
      component: ConfirmModal,
      props: {
        title: 'Cancel Subscription',
        message: 'Are you sure you want to cancel? Your subscription will remain active until the end of the current billing period. You can reactivate anytime before it expires.',
        confirmText: 'Cancel Subscription',
        type: 'warning',
        onConfirm: async () => {
          try {
            const res = await fetch('/api/billing/cancel', {
              method: 'POST', headers: authHeaders(),
              body: JSON.stringify({ company_id: user?.company_id }),
            });
            const d = await res.json();
            closeModal(modalId);
            openModal({ component: AlertModal, props: {
              title: d.success ? 'Subscription Cancelled' : 'Cancellation Failed',
              message: d.message || (d.success ? 'Your subscription will cancel at the end of the billing period.' : 'Failed to cancel subscription.'),
              type: d.success ? 'info' : 'danger',
            }});
          } catch {
            closeModal(modalId);
            openModal({ component: AlertModal, props: { title: 'Error', message: 'Failed to cancel subscription.', type: 'danger' } });
          }
        },
        onCancel: () => closeModal(modalId),
      }
    });
  };

  const RESOURCE_LABELS: Record<string, string> = {
    ai_requests: 'AI Requests',
    parser_documents: 'Document Parsing',
    evidence_uploads: 'Evidence Uploads',
    bulk_import_rows: 'Bulk Import Rows',
    storage_bytes: 'Storage',
  };

  const formatValue = (resource: string, value: number): string => {
    if (resource === 'storage_bytes') {
      if (value >= 1_073_741_824) return `${(value / 1_073_741_824).toFixed(1)} GB`;
      return `${(value / 1_048_576).toFixed(1)} MB`;
    }
    return value.toLocaleString();
  };

  const progressColor = (percentage: number, status: string): string => {
    if (status === 'unlimited') return 'bg-blue-500';
    if (status === 'blocked' || percentage >= 95) return 'bg-red-500';
    if (status === 'warning_95' || status === 'warning_80' || percentage >= 80) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const handleTopup = async (productId: string) => {
    try {
      const res = await fetch('/api/billing/topup-checkout', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ product_id: productId }),
      });
      const d = await res.json();
      if (d.success && d.url) window.location.href = d.url;
      else openModal({ component: AlertModal, props: { title: 'Top-up Unavailable', message: friendlyError(d.message) || 'Top-up checkout is not available at the moment.', type: 'info' } });
    } catch { openModal({ component: AlertModal, props: { title: 'Top-up Unavailable', message: 'Top-up checkout is not available at the moment. Please try again later.', type: 'info' } }); }
  };

  if (loading) return <div className="p-6 text-center text-slate-400">Loading billing information...</div>;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Billing & Plans</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your subscription, view invoices, and upgrade your plan</p>
      </div>

      {/* Current Subscription */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-6">
        <h2 className="font-bold text-slate-900 dark:text-white mb-4">Current Plan</h2>
        {subscription ? (
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">{subscription.plan_name}</div>
                <div className="text-sm text-slate-500">{subscription.tier} tier | {subscription.currency} {subscription.price}/month</div>
              </div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                subscription.status === 'active' ? 'bg-green-100 text-green-700' :
                subscription.status === 'trialing' ? 'bg-blue-100 text-blue-700' :
                subscription.status === 'past_due' ? 'bg-red-100 text-red-700' :
                'bg-slate-100 text-slate-600'
              }`}>{subscription.status}</span>
            </div>
            <div className="text-xs text-slate-500 mb-4">
              {subscription.next_billing_date && `Next billing: ${new Date(subscription.next_billing_date).toLocaleDateString('en-GB')}`}
            </div>
            <div className="flex gap-2">
              <button onClick={handlePortal}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700">
                Manage Subscription
              </button>
              {subscription.status === 'active' && (
                <button onClick={handleCancel}
                  className="px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg">
                  Cancel Subscription
                </button>
              )}
            </div>
            {/* Feature access */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Users', value: subscription.max_users < 0 ? 'Unlimited' : subscription.max_users },
                { label: 'AI Assistant', value: subscription.ai_assistant ? 'Included' : 'Not included' },
                { label: 'Gap Analysis', value: subscription.gap_analysis ? 'Included' : 'Not included' },
                { label: 'API Access', value: subscription.api_access ? 'Included' : 'Not included' },
              ].map(f => (
                <div key={f.label} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">{f.label}</div>
                  <div className="text-sm font-semibold text-slate-900 dark:text-white">{f.value}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-slate-500 mb-4">No active subscription. Choose a plan below to get started.</p>
          </div>
        )}
      </div>

      {/* Usage & Entitlements */}
      {entitlements?.resources?.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bold text-slate-900 dark:text-white">Usage & Entitlements</h2>
              {entitlements.billing_period && (
                <p className="text-xs text-slate-500 mt-1">
                  Current period: {new Date(entitlements.billing_period.start).toLocaleDateString('en-GB')} &ndash; {new Date(entitlements.billing_period.end).toLocaleDateString('en-GB')}
                </p>
              )}
            </div>
            {entitlements.plan_name && (
              <span className="text-xs font-semibold text-slate-500 bg-slate-100 dark:bg-slate-700 px-3 py-1 rounded-full">{entitlements.plan_name}</span>
            )}
          </div>

          {/* Warning banners */}
          {entitlements.resources.some((r: any) => r.status === 'blocked') && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
              <svg className="w-5 h-5 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span>One or more resources have reached their limit. Purchase additional credits or upgrade your plan to continue.</span>
            </div>
          )}
          {!entitlements.resources.some((r: any) => r.status === 'blocked') && entitlements.resources.some((r: any) => r.status === 'warning_95') && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
              <svg className="w-5 h-5 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span>You are approaching the limit on one or more resources. Consider purchasing additional credits soon.</span>
            </div>
          )}
          {!entitlements.resources.some((r: any) => r.status === 'blocked') && !entitlements.resources.some((r: any) => r.status === 'warning_95') && entitlements.resources.some((r: any) => r.status === 'warning_80') && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
              <svg className="w-5 h-5 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span>Some resources are nearing their limit. Keep an eye on your usage or top up early.</span>
            </div>
          )}

          {/* Resource cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {entitlements.resources.map((r: any) => {
              const label = RESOURCE_LABELS[r.resource] || r.resource;
              const pct = r.status === 'unlimited' ? 0 : Math.min(r.percentage ?? 0, 100);
              const topup = topupProducts.find((p: any) => p.resource_type === r.resource);
              return (
                <div key={r.resource} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">{label}</span>
                    {r.status === 'unlimited' ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Unlimited</span>
                    ) : r.status === 'blocked' ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">Limit reached</span>
                    ) : null}
                  </div>
                  {r.status !== 'unlimited' && (
                    <>
                      <div className="w-full h-2 bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden mb-2">
                        <div className={`h-full rounded-full transition-all ${progressColor(pct, r.status)}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                        <span>{formatValue(r.resource, r.used)} / {formatValue(r.resource, r.limit)}</span>
                        <span className="font-semibold">{pct.toFixed(0)}%</span>
                      </div>
                    </>
                  )}
                  {r.status === 'unlimited' && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{formatValue(r.resource, r.used)} used</div>
                  )}
                  {topup && r.status !== 'unlimited' && (
                    <button onClick={() => handleTopup(topup.id)}
                      className="mt-3 w-full px-3 py-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                      Buy More &middot; {topup.credit_amount.toLocaleString()} credits &middot; {topup.currency?.toUpperCase()} {(topup.price_pence / 100).toFixed(2)}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Plans */}
      {(!subscription || subscription.status !== 'active') && plans.length > 0 && (
        <div className="mb-6">
          <h2 className="font-bold text-slate-900 dark:text-white mb-4">Available Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.filter((p: any) => p.tier !== 'free').map((plan: any) => (
              <div key={plan.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
                <div className="text-lg font-bold text-slate-900 dark:text-white">{plan.name}</div>
                <div className="text-2xl font-bold text-blue-600 mt-2">{plan.currency} {plan.price}<span className="text-sm text-slate-400 font-normal">/{plan.billing_cycle}</span></div>
                <p className="text-xs text-slate-500 mt-2 mb-4">{plan.description}</p>
                <ul className="space-y-2 mb-6 text-xs text-slate-600 dark:text-slate-300">
                  <li>{plan.max_users < 0 ? 'Unlimited' : plan.max_users} users</li>
                  {plan.ai_assistant ? <li>AI Assistant included</li> : null}
                  {plan.gap_analysis ? <li>Gap Analysis included</li> : null}
                  {plan.api_access ? <li>API Access included</li> : null}
                </ul>
                <button onClick={() => handleCheckout(plan.id)}
                  className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700">
                  Subscribe
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoices */}
      {invoices.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-100 dark:border-slate-700">
            <h2 className="font-bold text-slate-900 dark:text-white">Invoice History</h2>
          </div>
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Invoice</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Date</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {invoices.map((inv: any) => (
                <tr key={inv.id}>
                  <td className="px-4 py-3 text-left text-slate-900 dark:text-white font-mono text-xs">{inv.number || inv.id}</td>
                  <td className="px-4 py-3 text-left text-xs text-slate-500">{inv.date ? new Date(inv.date).toLocaleDateString('en-GB') : '-'}</td>
                  <td className="px-4 py-3 text-left text-slate-900 dark:text-white">{inv.currency} {inv.amount}</td>
                  <td className="px-4 py-3 text-left">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{inv.status}</span>
                  </td>
                  <td className="px-4 py-3 text-left">
                    {inv.pdf_url && <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Download</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
